import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, FileTextIcon, CreditCardIcon, BellIcon, DatabaseIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { SOLUTION_IMAGES, solutionCopy, solutionBenefits, type Bi } from '../../data/animalsSolution';
import { PetPhone } from './AnimalsCards';
import { ReminderCard, AdminRegister } from './SolutionMocks';
const EASE = [0.23, 1, 0.32, 1] as const;
const BENEFIT_ICONS = {
  register: FileTextIcon,
  payment: CreditCardIcon,
  reminders: BellIcon,
  data: DatabaseIcon
} as const;
const PHOTO_MASK = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 20%)',
  maskImage: 'linear-gradient(to right, transparent 0%, #000 20%)'
};
export function AnimalsSolution() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const c = solutionCopy;
  const reveal = {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const message = <>
      <p className="font-sans text-[13px] font-bold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-329" data-mp="ad0dutigs24a">{pick(c.eyebrow)}</p>
      <h2 className="font-serif text-[2.6rem] sm:text-[3.4rem] 2xl:text-[4.3rem] leading-[1.02] tracking-tight mt-5" data-id="element-330" data-mp="h6uaq6bv8d0">
        <span className="block text-[#121A3A]" data-id="element-331" data-mp="3iuo7i0hts1ev">{pick(c.line1)}</span>
        <span className="block text-[#4F46E5]" data-id="element-332" data-mp="3itd3bzkgwrn8">{pick(c.line2)}</span>
        <span className="block text-[#4F46E5]" data-id="element-333" data-mp="3nnhbgm5gz5pw">{pick(c.line3)}</span>
      </h2>
      <p className="font-sans text-[18px] md:text-[19px] text-[#5B6478] leading-relaxed mt-6 max-w-[580px]" data-id="element-334" data-mp="38y1ntf18jgef">
        {pick(c.bodyBefore)}
        {pick(c.bodyAfter)}
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-335" data-mp="2zvscwo221l0i">
        <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
        location: 'animals_solution',
        cta: 'see_in_action'
      })} className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4F46E5] px-8 py-4 font-sans text-[16px] font-medium text-white whitespace-nowrap shadow-[0_14px_30px_-14px_rgba(79,70,229,0.7)] hover:bg-[#4338CA] transition-colors duration-150" data-id="element-336" data-mp="2zyoc0fpn2fqj">
          {pick(c.primary)}
          <ArrowRightIcon className="w-4 h-4" strokeWidth={2.2} aria-hidden="true" data-id="element-337" data-mp="wp30e1irs4uu" />
        </Link>
        <Link to="/request-demo" onClick={() => trackEvent('CTA Clicked', {
        location: 'animals_solution',
        cta: 'talk_to_team'
      })} className="inline-flex items-center justify-center rounded-full border-[1.5px] border-[#4F46E5] bg-white px-8 py-4 font-sans text-[16px] font-medium text-[#4F46E5] whitespace-nowrap hover:bg-[#F5F4FE] transition-colors duration-150" data-id="element-338" data-mp="1tcrabw8ogr3b">
          {pick(c.secondary)}
        </Link>
      </div>
    </>;
  return <div className="relative w-full" data-id="element-339" data-mp="3yuqewye0qrc">
      {/* Desktop — layered composition */}
      <div className="hidden xl:block relative max-w-[1760px] mx-auto min-h-[800px]" data-id="element-340" data-mp="2fmzo0oazks65">
        <div className="absolute inset-y-0 right-0 w-[70%]" aria-hidden="true" data-id="element-341" data-mp="2klkf9y89g1ig">
          <img src={SOLUTION_IMAGES.scene} alt="" className="w-full h-full object-cover object-[85%_30%]" style={PHOTO_MASK} data-id="element-342" data-mp="2kj6jto9o4nad" />
        </div>
        <span className="sr-only" data-id="element-343" data-mp="2te4ep0wdma4c">{pick(c.imageAlt)}</span>
        <svg className="absolute bottom-0 left-0 w-full h-[170px] pointer-events-none z-[5]" viewBox="0 0 1440 170" preserveAspectRatio="none" aria-hidden="true" data-id="element-344" data-mp="2tfpb6w29434u">
          <path d="M0 170 L0 90 C 480 150, 1000 150, 1440 60 L1440 170 Z" fill="#FFFFFF" data-id="element-345" data-mp="33aqx7j18cp37" />
        </svg>

        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative z-10 w-[38%] pl-6 2xl:pl-10 pt-16 pb-48" data-id="element-346" data-mp="38bn322556lb9">
          {message}
        </motion.div>

        {/* Reminder + connector */}
        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.1,
        ease: EASE
      }} className="absolute z-10 top-[48px] left-[63%] w-[20%]" data-id="element-347" data-mp="1dz6z57ztjj9k">
          <ReminderCard isFR={isFR} data-id="element-348" data-mp="mnzpuymyc43v" />
          <svg className="block ml-[20%] w-[60%] h-[48px]" viewBox="0 0 200 48" preserveAspectRatio="none" fill="none" aria-hidden="true" data-id="element-349" data-mp="mmy522g4w38y">
            <path d="M20 0 V18 Q20 28 30 28 H170 Q180 28 180 38 V48" stroke="#6D66EE" strokeWidth="1.6" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" data-id="element-350" data-mp="wmox7xh8265v" />
          </svg>
        </motion.div>

        {/* Resident phone */}
        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.15,
        ease: EASE
      }} className="absolute z-20 top-[200px] left-[59.5%]" data-id="element-351" data-mp="3x9qvl0i51ga">
          <PetPhone isFR={isFR} data-id="element-352" data-mp="3v65l7hrqv9tr" />
        </motion.div>

        {/* Organization dashboard */}
        <motion.div {...reveal} transition={{
        duration: 0.3,
        delay: 0.2,
        ease: EASE
      }} className="absolute z-10 top-[240px] -right-[3%] w-[29%]" data-id="element-353" data-mp="dp5n0qj12hhv">
          <AdminRegister isFR={isFR} data-id="element-354" data-mp="3hcecucpr1vzm" />
        </motion.div>
      </div>

      {/* Tablet / mobile — stacked */}
      <div className="xl:hidden px-6 max-w-[1100px] mx-auto" data-id="element-355" data-mp="3cemmvo8o0jy2">
        <motion.div {...reveal} transition={{
        duration: 0.3,
        ease: EASE
      }} data-id="element-356" data-mp="3cc0czhyaxsco">
          {message}
        </motion.div>
        <img src={SOLUTION_IMAGES.scene} alt={pick(c.imageAlt)} className="mt-8 w-full aspect-[4/3] md:aspect-[16/9] rounded-[24px] object-cover object-[30%_30%]" data-id="element-357" data-mp="1wm900195zv5r" />
        <div className="mt-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start" data-id="element-358" data-mp="1mwfwlnj6emcl">
          <div className="flex justify-center" data-id="element-359" data-mp="2amjpp0yqn3pc">
            <PetPhone isFR={isFR} data-id="element-360" data-mp="2amti0v799my7" />
          </div>
          <div className="space-y-5 min-w-0" data-id="element-361" data-mp="2fl4sn85dk1hh">
            <ReminderCard isFR={isFR} data-id="element-362" data-mp="2fkuzjbp19e7k" />
            <AdminRegister isFR={isFR} data-id="element-363" data-mp="21llwzetr2im2" />
          </div>
        </div>
      </div>

      {/* Benefits on the white wave */}
      <div className="relative bg-white" data-id="element-364" data-mp="26k6vn9tcsu8e">
        <ul className="max-w-[1760px] mx-auto px-6 2xl:px-10 pt-12 xl:pt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x divide-[#E8E8F0]" data-id="element-365" data-mp="26j5c0gy9uzf2">
          {solutionBenefits.map(b => {
          const Icon = BENEFIT_ICONS[b.key];
          return <li key={b.key} className="flex items-start gap-4 lg:px-8 first:lg:pl-2" data-id="element-366" data-mp="2wte26o44d1mb">
                <span className="w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-367" data-mp="2rwnna441wc2h">
                  <Icon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} data-id="element-368" data-mp="2n32zt60330hj" />
                </span>
                <span className="min-w-0 pt-1" data-id="element-369" data-mp="2j32a2p7jb2v6">
                  <span className="block font-sans text-[17px] font-semibold text-[#121A3A]" data-id="element-370" data-mp="2e4r08eh8oscy">{pick(b.title)}</span>
                  <span className="block font-sans text-[15px] text-[#5B6478] leading-snug mt-1" data-id="element-371" data-mp="29438trp3m9i7">{pick(b.body)}</span>
                </span>
              </li>;
        })}
        </ul>
      </div>
    </div>;
}